import InactivityPrompt from './InactivityPrompt';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRetziaUser } from '../hooks/useRetziaUser';
import { replaceWithSession } from '../services/sessionNavigation';
import { changeConversation, loadConversation, RestoredConversation } from '../services/conversationPersistence';
import './ConversationLifecycle.css';

export interface ManagedConversationProps {
  initialConversation: RestoredConversation;
  requestNew: (flush: () => Promise<void>) => Promise<void>;
  onPersistenceConflict: () => void;
}
export default function ConversationLifecycle({language, mode, children}: {
  language: string; mode: 'conversation' | 'task_review' | 'grammar';
  children: (props: ManagedConversationProps) => React.ReactNode;
}) {
  const {currentUser, checking} = useRetziaUser();
  const [conversation,setConversation] = useState<RestoredConversation | null>(null);
  const [decision,setDecision] = useState(false);
  const [busy,setBusy] = useState(false);
  const busyRef=useRef(false);
  const [confirmNew,setConfirmNew]=useState(false);
  const flushRef=useRef<()=>Promise<void>>(async()=>{});
  const [error,setError] = useState('');
  const [fatal,setFatal] = useState(false);
  const noun = mode === 'task_review' ? 'actividad' : 'conversación';
  const conflict = useCallback(() => {
    setFatal(true);
    setError('La conversación cambió en otra pestaña. Recarga para continuar con la actual.');
  }, []);
  useEffect(() => {
    if(checking)return;
    if(!currentUser){replaceWithSession('/login');return;}
    let cancelled=false;
    (async()=>{
      try {
        let restored=await loadConversation(language,mode);
        if(!restored){await changeConversation(language,mode,0,'ensure');restored=await loadConversation(language,mode);}
        if(!restored)throw new Error('No conversation');
        if(!cancelled){setConversation(restored);setDecision(mode !== 'grammar' && restored.inactive24h);}
      } catch {if(!cancelled){setError('No se pudo recuperar tu conversación. Recarga para volver a intentarlo.');setFatal(true);}}
    })();
    return ()=>{cancelled=true;};
  },[language,mode,currentUser,checking]);
  const beginNew = async (flush:()=>Promise<void>) => {
    if(busyRef.current||!conversation)return;
    busyRef.current=true;setConfirmNew(false);
    setBusy(true);setError('');
    try {
      await flush();
      await changeConversation(language,mode,conversation.id,'new');
      const fresh=await loadConversation(language,mode);
      if(!fresh)throw new Error('No conversation');
      setConversation(fresh);setDecision(false);
    } catch(e) {
      if(e instanceof Error && e.message==='conversation_changed')conflict();
      else setError('No se pudo iniciar una nueva. Recarga para comprobar cuál está disponible antes de volver a intentarlo.');
    } finally {busyRef.current=false;setBusy(false);}
  };
  return <>
    {error&&<div className="retzia-lifecycle-notice" role="alert">{error} <button onClick={()=>window.location.reload()}>Recargar</button></div>}
    {!fatal&&(!conversation ? <p role="status">Recuperando tu conversación…</p> : decision ?
      <InactivityPrompt noun={noun} name={currentUser?.name} busy={busy} onNew={()=>void beginNew(async()=>{})} onContinue={()=>setDecision(false)} /> : <div key={conversation.id} {...{inert:busy || confirmNew ? '' : undefined}}>
        {children({initialConversation:conversation,requestNew:async flush=>{flushRef.current=flush;setConfirmNew(true);},onPersistenceConflict:conflict})}
      </div>)}
    {confirmNew&&<div className="retzia-lifecycle-modal" onKeyDown={e=>{if(e.key==='Escape')setConfirmNew(false);}}>
      <section role="alertdialog" aria-modal="true" aria-labelledby="new-conversation-title" aria-describedby="new-conversation-description" className="retzia-lifecycle-card">
        <h2 id="new-conversation-title">¿Iniciar una nueva {noun}?</h2>
        <p id="new-conversation-description">Empezarás desde cero y ya no podrás volver a esta {noun} desde aquí.</p>
        <button autoFocus onClick={()=>setConfirmNew(false)}>Cancelar</button>
        <button onClick={()=>void beginNew(flushRef.current)}>Iniciar nueva {noun}</button>
      </section>
    </div>}
    {busy&&<div className="retzia-lifecycle-busy" role="status">Guardando e iniciando…</div>}
  </>;
}
